import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import OtterIcon from "@/components/app/OtterIcon";

/**
 * Smart anchor link — if we're on `to`'s pathname already, smooth-scroll
 * to the hash. Otherwise navigate to the route + hash (Landing/Pricing's
 * scroll-on-hash effect picks it up).
 */
function NavHashLink({ to, hash, children, testid }) {
  const navigate = useNavigate();
  const loc = useLocation();
  const handleClick = (e) => {
    e.preventDefault();
    if (loc.pathname === to) {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate(`${to}#${hash}`);
    }
  };
  return (
    <a
      href={`${to}#${hash}`}
      onClick={handleClick}
      className="hover:underline underline-offset-4"
      data-testid={testid}
    >
      {children}
    </a>
  );
}

export default function MarketingNav() {
  const { user } = useAuth();
  return (
    <header className="sticky top-0 z-40 border-b border-border bg-white/80 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
          <div className="w-7 h-7 bg-foreground grid place-items-center text-background">
            <OtterIcon size={18} />
          </div>
          <span className="font-semibold tracking-tight text-lg">ArloSEO</span>
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm">
          <NavHashLink to="/" hash="features" testid="nav-features">Features</NavHashLink>
          <NavHashLink to="/" hash="how" testid="nav-how">How it works</NavHashLink>
          <Link to="/pricing" className="hover:underline underline-offset-4" data-testid="nav-pricing-page">Pricing</Link>
          <NavHashLink to="/pricing" hash="faq" testid="nav-faq">FAQ</NavHashLink>
          <Link to="/free-audit" className="hover:underline underline-offset-4 font-medium" data-testid="nav-free-audit">Free audit</Link>
          <Link to="/quick-start" className="hover:underline underline-offset-4 font-medium" data-testid="nav-quick-start">Quick start</Link>
          <Link to="/social" className="hover:underline underline-offset-4 font-medium" data-testid="nav-social">Social writer</Link>
          <Link to="/blog" className="hover:underline underline-offset-4" data-testid="nav-blog">Blog</Link>
          <Link to="/docs" className="hover:underline underline-offset-4" data-testid="nav-docs">Docs</Link>
        </nav>
        <div className="flex items-center gap-2">
          {user && typeof user === "object" ? (
            <Link to="/app">
              <Button data-testid="nav-app-btn" className="rounded-none">Open app →</Button>
            </Link>
          ) : (
            <>
              <Link to="/login">
                <Button variant="ghost" className="rounded-none" data-testid="nav-login-btn">Sign in</Button>
              </Link>
              <Link to="/register">
                <Button className="rounded-none bg-foreground hover:bg-foreground/90" data-testid="nav-register-btn">
                  Start free
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
